import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Trophy, Medal, Award, Clock, Star, Eye, EyeOff, Crown } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RankingPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [ranking, setRanking] = useState([]);
  const [totalCertified, setTotalCertified] = useState(0);
  const [myPosition, setMyPosition] = useState(null);
  const [showInRanking, setShowInRanking] = useState(true);
  const [updatingPreference, setUpdatingPreference] = useState(false);

  useEffect(() => {
    fetchRanking();
    if (isAuthenticated) {
      fetchMyPosition();
      fetchPreference();
    }
  }, [isAuthenticated]);

  const fetchRanking = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/ranking`);
      setRanking(response.data.ranking);
      setTotalCertified(response.data.total_certified_users);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.error('Error fetching ranking:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchMyPosition = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/ranking/my-position`
      );
      setMyPosition(response.data);
    } catch (err) {
      // Position not available - user may not be in ranking
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.debug('Posição no ranking indisponível:', err?.response?.status);
      }
    }
  };

  const fetchPreference = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/ranking/preference`
      );
      setShowInRanking(response.data.show_in_ranking);
    } catch (err) {
      // Preference not set - use default
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.debug('Preferência de ranking não definida:', err?.response?.status);
      }
    }
  };

  const handleToggleVisibility = async (checked) => {
    setUpdatingPreference(true);
    try {
      await axios.put(
        `${API_URL}/api/ranking/preference`,
        { show_in_ranking: checked }
      );
      setShowInRanking(checked);
      // Recarregar ranking e posição
      fetchRanking();
      fetchMyPosition();
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.error('Error updating preference:', err);
      }
    } finally {
      setUpdatingPreference(false);
    }
  };

  const getMedalIcon = (position) => {
    switch (position) {
      case 1:
        return <Crown className="w-8 h-8 text-yellow-400" />;
      case 2:
        return <Medal className="w-7 h-7 text-gray-300" />;
      case 3:
        return <Medal className="w-6 h-6 text-amber-600" />;
      default:
        return <span className="text-xl font-bold text-gray-500">#{position}</span>;
    }
  };

  const getPositionStyle = (position) => {
    switch (position) {
      case 1:
        return 'bg-gradient-to-r from-yellow-900/40 to-yellow-800/20 border-yellow-500/50';
      case 2:
        return 'bg-gradient-to-r from-gray-800/40 to-gray-700/20 border-gray-400/50';
      case 3:
        return 'bg-gradient-to-r from-amber-900/40 to-amber-800/20 border-amber-600/50';
      default:
        return 'bg-zinc-900 border-zinc-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-red-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white">Carregando ranking...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black pt-24 pb-12">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            className="text-white hover:text-red-500 mb-4"
          >
            <ArrowLeft className="mr-2" size={20} />
            Voltar ao Início
          </Button>
          
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-yellow-500/20 rounded-full mb-4">
              <Trophy className="w-10 h-10 text-yellow-500" />
            </div>
            <h1 className="text-4xl font-bold text-white mb-2">Ranking de Certificados</h1>
            <p className="text-gray-400">
              Top 20 alunos com mais certificados da AJ BIM Academy
            </p>
            <p className="text-sm text-gray-500 mt-2">
              {totalCertified} {totalCertified === 1 ? 'aluno certificado' : 'alunos certificados'}
            </p>
          </div>
        </div>

        {/* My Position Card (if authenticated) */}
        {isAuthenticated && myPosition && (
          <Card className="bg-gradient-to-r from-red-900/30 to-zinc-900 border-red-800 mb-8">
            <CardContent className="pt-6">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-red-600/20 rounded-full flex items-center justify-center">
                    {myPosition.in_ranking ? (
                      <span className="text-2xl font-bold text-red-500">#{myPosition.position}</span>
                    ) : (
                      <Award className="w-8 h-8 text-red-500" />
                    )}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">Sua Posição</h3>
                    {myPosition.show_in_ranking === false ? (
                      <p className="text-gray-400 text-sm">Você optou por não aparecer no ranking</p>
                    ) : myPosition.certificate_count === 0 ? (
                      <p className="text-gray-400 text-sm">Complete cursos para entrar no ranking!</p>
                    ) : (
                      <div className="flex items-center gap-4 text-sm text-gray-400">
                        <span className="flex items-center gap-1">
                          <Award size={14} className="text-yellow-500" />
                          {myPosition.certificate_count} {myPosition.certificate_count === 1 ? 'certificado' : 'certificados'}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock size={14} />
                          {myPosition.total_hours}h de estudo
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Visibility Toggle */}
                <div className="flex items-center gap-3 bg-zinc-900 rounded-lg px-4 py-3">
                  <div className="flex items-center gap-2">
                    {showInRanking ? (
                      <Eye size={18} className="text-green-500" />
                    ) : (
                      <EyeOff size={18} className="text-gray-500" />
                    )}
                    <span className="text-sm text-gray-300">
                      {showInRanking ? 'Visível no ranking' : 'Oculto do ranking'}
                    </span>
                  </div>
                  <Switch
                    checked={showInRanking}
                    onCheckedChange={handleToggleVisibility}
                    disabled={updatingPreference}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Ranking List */}
        {ranking.length === 0 ? (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="py-16 text-center">
              <Trophy className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-white mb-2">
                Nenhum aluno no ranking ainda
              </h2>
              <p className="text-gray-400 mb-6">
                Seja o primeiro a conquistar certificados!
              </p>
              <Button
                onClick={() => navigate('/')}
                className="bg-red-600 hover:bg-red-700"
              >
                Explorar Cursos
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {ranking.map((item, idx) => (
              <Card 
                key={item.user_id} 
                className={`border transition-all hover:scale-[1.01] ${getPositionStyle(item.position)}`}
              >
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {/* Position */}
                      <div className="w-14 h-14 flex items-center justify-center">
                        {getMedalIcon(item.position)}
                      </div>
                      
                      {/* User Info */}
                      <div>
                        <h3 className={`font-semibold ${item.position <= 3 ? 'text-lg' : 'text-base'} text-white`}>
                          {item.display_name}
                        </h3>
                        <div className="flex items-center gap-3 text-sm text-gray-400">
                          <span className="flex items-center gap-1">
                            <Clock size={14} />
                            {item.total_hours}h
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Certificates Count */}
                    <div className="text-right">
                      <div className={`flex items-center gap-2 ${item.position === 1 ? 'text-yellow-400' : 'text-white'}`}>
                        <Award size={item.position <= 3 ? 24 : 20} className={item.position <= 3 ? 'text-yellow-500' : 'text-red-500'} />
                        <span className={`font-bold ${item.position <= 3 ? 'text-2xl' : 'text-xl'}`}>
                          {item.certificate_count}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500">
                        {item.certificate_count === 1 ? 'certificado' : 'certificados'}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Call to Action */}
        {!isAuthenticated && (
          <div className="mt-8 text-center">
            <p className="text-gray-400 mb-4">
              Faça login para ver sua posição e participar do ranking!
            </p>
            <Button
              onClick={() => navigate('/')}
              className="bg-red-600 hover:bg-red-700"
            >
              Fazer Login
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RankingPage;
